import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, HeartPulse, Cpu, Palette, Scale, TrendingUp } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";

const ICONS = {
  healthcare: HeartPulse,
  technology: Cpu,
  arts_culture: Palette,
  law_governance: Scale,
  commerce_economy: TrendingUp,
};
const ACCENTS = {
  healthcare: "#10B981",
  technology: "#06B6D4",
  arts_culture: "#F59E0B",
  law_governance: "#4F46E5",
  commerce_economy: "#64748B",
};

export default function Quiz() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [domainIdx, setDomainIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // { domain_key: [scores...] }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quiz/questions");
        setDomains(data.domains || []);
        const init = {};
        for (const d of data.domains || []) init[d.key] = Array(d.questions.length).fill(5);
        setAnswers(init);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalQuestions = useMemo(
    () => domains.reduce((acc, d) => acc + d.questions.length, 0),
    [domains],
  );
  const answeredSoFar = useMemo(() => {
    let n = 0;
    for (let di = 0; di < domainIdx; di++) n += domains[di]?.questions.length || 0;
    n += qIdx;
    return n;
  }, [domainIdx, qIdx, domains]);
  const progress = totalQuestions ? (answeredSoFar / totalQuestions) * 100 : 0;

  if (loading)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 font-ui uppercase text-xs tracking-widest text-[var(--es-ink-soft)]">
          Loading questions…
        </p>
      </div>
    );

  if (error)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 text-red-600 font-body" data-testid="quiz-error">
          {error}
        </p>
      </div>
    );

  const currentDomain = domains[domainIdx];
  const currentQuestion = currentDomain.questions[qIdx];
  const Icon = ICONS[currentDomain.key];
  const accent = ACCENTS[currentDomain.key];
  const currentScore = answers[currentDomain.key]?.[qIdx] ?? 5;

  const setScore = (val) => {
    setAnswers((prev) => {
      const next = { ...prev };
      next[currentDomain.key] = next[currentDomain.key].slice();
      next[currentDomain.key][qIdx] = val;
      return next;
    });
  };

  const isFirst = domainIdx === 0 && qIdx === 0;
  const isLast = domainIdx === domains.length - 1 && qIdx === currentDomain.questions.length - 1;

  const goNext = async () => {
    if (qIdx < currentDomain.questions.length - 1) {
      setQIdx(qIdx + 1);
      return;
    }
    if (domainIdx < domains.length - 1) {
      setDomainIdx(domainIdx + 1);
      setQIdx(0);
      return;
    }
    // submit
    if (!user || user === false) {
      // require auth to save → redirect to register
      sessionStorage.setItem("es_pending_answers", JSON.stringify(answers));
      navigate("/register");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessments", { answers });
      navigate(`/results/${data.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (qIdx > 0) {
      setQIdx(qIdx - 1);
      return;
    }
    if (domainIdx > 0) {
      const prev = domainIdx - 1;
      setDomainIdx(prev);
      setQIdx(domains[prev].questions.length - 1);
    }
  };

  return (
    <div className="App" data-testid="quiz-page">
      <Navbar />
      {/* progress */}
      <div className="sticky top-16 z-30 bg-white/90 backdrop-blur border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center gap-4">
          <p
            className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)] w-32 shrink-0"
            data-testid="quiz-progress-label"
          >
            {answeredSoFar + 1} / {totalQuestions}
          </p>
          <div className="flex-1 h-[3px] bg-[var(--es-line)]">
            <div
              className="h-full bg-[var(--es-blue)] transition-all duration-300"
              style={{ width: `${progress}%` }}
              data-testid="quiz-progress-bar"
            />
          </div>
          <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)] hidden sm:block">
            {currentDomain.label}
          </p>
        </div>
      </div>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex items-center gap-3 mb-8">
          <div
            className="w-12 h-12 flex items-center justify-center"
            style={{ background: accent + "1A", color: accent }}
            data-testid="quiz-domain-icon"
          >
            <Icon size={22} strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">
              Pillar 0{domainIdx + 1} · {currentDomain.label}
            </p>
            <p className="font-body text-[var(--es-ink-soft)] text-sm italic">
              {currentDomain.tagline}
            </p>
          </div>
        </div>

        <div className="slide-in" key={`${domainIdx}-${qIdx}`}>
          <h2
            className="font-display text-3xl sm:text-4xl md:text-5xl text-[var(--es-ink)] tracking-tight leading-[1.1]"
            data-testid="quiz-question-text"
          >
            {currentQuestion}
          </h2>
          <p className="font-body text-[var(--es-ink-soft)] mt-4">
            Move the slider to where you stand today — 1 is foundational, 10 is mastery.
          </p>

          <div className="mt-12">
            <div className="flex items-baseline justify-between mb-4">
              <span className="font-ui text-[11px] uppercase tracking-widest text-[var(--es-ink-soft)]">
                Your reading
              </span>
              <span
                className="font-display text-6xl text-[var(--es-blue)] leading-none"
                data-testid="quiz-current-score"
              >
                {currentScore}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={currentScore}
              onChange={(e) => setScore(Number(e.target.value))}
              className="es-slider"
              data-testid="quiz-slider-input"
              aria-label="Score from 1 to 10"
            />
            <div className="flex justify-between mt-3 font-ui text-[10px] uppercase tracking-widest text-[var(--es-ink-soft)]">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <span key={n} className={n === currentScore ? "text-[var(--es-blue)] font-semibold" : ""}>
                  {n}
                </span>
              ))}
            </div>
            <div className="mt-3 flex justify-between font-ui text-[10px] uppercase tracking-widest text-[var(--es-ink-soft)]">
              <span>Foundational</span>
              <span>Developing</span>
              <span>Applied</span>
              <span>Mastery</span>
            </div>
          </div>

          <div className="mt-16 flex items-center justify-between border-t border-[var(--es-line)] pt-8">
            <button
              onClick={goBack}
              disabled={isFirst}
              className="font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)] hover:text-[var(--es-blue)] disabled:opacity-30 inline-flex items-center gap-2"
              data-testid="quiz-back-button"
            >
              <ArrowLeft size={14} strokeWidth={1.6} /> Back
            </button>
            <button
              onClick={goNext}
              disabled={submitting}
              className="bg-[var(--es-blue)] text-white px-8 py-4 hover:bg-[var(--es-blue-hover)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="quiz-next-button"
            >
              {submitting ? "Calculating…" : isLast ? "See my Erudition Score" : "Next"}
              {!submitting && <ArrowRight size={14} strokeWidth={1.6} />}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
